import { cancel, invoke, on } from "@hybird/core/bridge/hybirdBridge";
import { BridgeMethod } from "@hybird/core/bridge/core";

/**
 * YkySystem 高拍仪系统管理类
 * 提供系统信息获取、网络配置、存储管理等功能
 */
export class YkySystem extends BridgeMethod {
  constructor() {
    super();
  }

  /**
   * 获取系统版本信息
   * @returns {Promise<Object>} 返回包含Android版本和硬件版本的对象
   * @example
   * {
   *   data: {
   *     android: "11",
   *     hardware: "v1.0.0"
   *   }
   * }
   */
  async ykySystemVersion() {
    return await invoke("ykySystemVersion");
  }

  /**
   * 获取设备类型
   * @returns {Promise<Object>} 返回设备类型信息
   * @example
   * {
   *   data: {
   *     type: "YKY_DEVICE_TYPE_A"
   *   }
   * }
   */
  async ykyDeviceType() {
    return await invoke("ykyDeviceType");
  }

  /**
   * 获取以太网类型
   * @returns {Promise<Object>} 返回以太网连接类型
   * @example
   * {
   *   data: {
   *     type: "ETHERNET_TYPE_STATIC" | "ETHERNET_TYPE_DHCP"
   *   }
   * }
   */
  async ykyEthType() {
    return await invoke("ykyEthType");
  }

  /**
   * 清理系统缓存
   * 清除应用和系统的临时缓存文件，释放存储空间
   * @returns {Promise<void>} 无返回值
   */
  async ykyClearCache() {
    return await invoke("ykyClearCache");
  }

  /**
   * 获取网络信息
   * @returns {Promise<Object>} 返回详细的网络配置信息
   * @example
   * {
   *   data: {
   *     wifiMac: "aa:bb:cc:dd:ee:ff",
   *     ethMac: "11:22:33:44:55:66",
   *     ipAddress: "192.168.1.100",
   *     netmask: "255.255.255.0",
   *     gateway: "192.168.1.1",
   *     dns1: "8.8.8.8",
   *     dns2: "8.8.4.4"
   *   }
   * }
   */
  async ykyNetworkInfo() {
    return await invoke("ykyNetworkInfo");
  }

  /**
   * 设置网络IP配置
   * 可以配置为DHCP自动获取或静态IP模式
   * @param {Object} config 网络配置参数
   * @param {boolean} config.dhcp 是否使用DHCP，true为自动获取，false为静态IP
   * @param {string} config.ipAddress IP地址（静态IP模式时必填）
   * @param {string} config.netmask 子网掩码（静态IP模式时必填）
   * @param {string} config.gateway 网关地址（静态IP模式时必填）
   * @param {string} config.dns1 主DNS服务器
   * @param {string} config.dns2 备用DNS服务器
   * @returns {Promise<void>} 无返回值
   * @example
   * // DHCP模式
   * await ykySetStaticIp({ dhcp: true });
   *
   * // 静态IP模式
   * await ykySetStaticIp({
   *   dhcp: false,
   *   ipAddress: '192.168.1.100',
   *   netmask: '255.255.255.0',
   *   gateway: '192.168.1.1',
   *   dns1: '8.8.8.8',
   *   dns2: '8.8.4.4'
   * });
   */
  async ykySetStaticIp(config = {}) {
    const params = {
      dhcp: config.dhcp || false,
      ipAddress: config.ipAddress || "",
      netmask: config.netmask || "",
      gateway: config.gateway || "",
      dns1: config.dns1 || "",
      dns2: config.dns2 || "",
    };
    return await invoke("ykySetStaticIp", { params });
  }

  /**
   * 系统固件更新
   * 触发系统固件更新流程，设备将自动下载并安装最新固件
   * @returns {Promise<void>} 无返回值
   * @warning 更新过程中设备可能会重启，请确保设备电源稳定
   */
  async ykyUpdateSystem() {
    return await invoke("ykyUpdateSystem");
  }

  /**
   * 获取存储信息
   * @returns {Promise<Object>} 返回设备存储空间详细信息
   * @example
   * {
   *   data: {
   *     total: 32000000000,    // 总容量（字节）
   *     free: 16000000000,     // 可用空间（字节）
   *     used: 16000000000,     // 已使用空间（字节）
   *     image: 2000000000,     // 图片文件占用（字节）
   *     video: 5000000000,     // 视频文件占用（字节）
   *     app: 3000000000,       // 应用程序占用（字节）
   *     system: 4000000000,    // 系统文件占用（字节）
   *     other: 2000000000      // 其他文件占用（字节）
   *   }
   * }
   */
  async ykyStorageInfo() {
    return await invoke("ykyStorageInfo");
  }

  /**
   * 打开系统网络设置
   * @returns {Promise<void>} 无返回值
   */
  async openNetworkSettings() {
    return await invoke("openNetworkSettings");
  }
}
